import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api, fmtDate, fmtMonth } from "@/lib/api";
import { imageUrl } from "@/components/ImageUpload";
import { MapPin, CalendarDays, Users, Search, Compass } from "lucide-react";

const RADIUS_OPTIONS = [10, 25, 50, 100, 250, 500];

export default function Marketplace() {
    const [events, setEvents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filters, setFilters] = useState({ near: "", radius_miles: 50, category_id: "", date_from: "" });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        // Strip empty params so we don't accidentally send `near=`
        const params = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
        );
        try {
            const [evRes, catRes] = await Promise.all([
                api.get("/events/public", { params }),
                api.get("/categories"),
            ]);
            setEvents(evRes.data);
            setCategories(catRes.data);
        } catch (err) {
            // Without this, a single failed request (flaky mobile network,
            // backend cold start, DNS hiccup) would leave the page stuck on
            // "Loading…" forever because the effect's `setLoading(false)`
            // never ran. Capture an inline error + offer a retry instead.
            setError(err?.response?.data?.detail || "Couldn't load events. Check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const clearNear = () => {
        setFilters({ ...filters, near: "" });
    };

    return (
        <Shell>
            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                    <div className="label-base">Marketplace</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">Upcoming health fairs</h1>
                    <p className="mt-3 text-brand-mute max-w-2xl">Discover health fairs hosted by leading employers. Search by location and radius, filter by date and category.</p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3" data-testid="filters-panel">
                    <div className="rounded-xl border border-brand-line bg-white p-5 sticky top-24">
                        <div className="font-heading font-bold mb-4 flex items-center gap-2"><Search size={16} /> Filters</div>
                        <div className="space-y-4">
                            <div>
                                <label className="label-base flex items-center gap-1"><Compass size={12} /> Near</label>
                                <input
                                    data-testid="filter-near"
                                    className="input-base"
                                    placeholder="City, state, or zip"
                                    value={filters.near}
                                    onChange={(e) => setFilters({ ...filters, near: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                                />
                                <p className="text-xs text-brand-mute mt-1.5">e.g. "Arlington, VA" or "20001". We'll geocode it and find events nearby.</p>
                            </div>
                            <div>
                                <label className="label-base">Within</label>
                                <select data-testid="filter-radius" className="input-base" value={filters.radius_miles} onChange={(e) => setFilters({ ...filters, radius_miles: parseInt(e.target.value, 10) })}>
                                    {RADIUS_OPTIONS.map((r) => (
                                        <option key={r} value={r}>{r} miles</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="label-base">Category</label>
                                <select data-testid="filter-category" className="input-base" value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}>
                                    <option value="">All categories</option>
                                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-base">Date from</label>
                                <input data-testid="filter-date" type="date" className="input-base" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
                            </div>
                            <div className="flex gap-2">
                                <button data-testid="filter-apply" onClick={load} className="pill btn-forest flex-1 h-10 font-semibold">Apply</button>
                                <button data-testid="filter-reset" onClick={() => { setFilters({ near: "", radius_miles: 50, category_id: "", date_from: "" }); setTimeout(load, 0); }} className="pill px-4 h-10 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone">Reset</button>
                            </div>
                        </div>
                    </div>
                </aside>

                <div className="lg:col-span-9">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                        <div className="text-sm text-brand-mute" data-testid="events-count">
                            {loading ? "Loading…" : `${events.length} event${events.length === 1 ? "" : "s"}${filters.near ? ` within ${filters.radius_miles} mi of "${filters.near}"` : ""}`}
                        </div>
                        {filters.near && !loading && (
                            <button onClick={clearNear} className="text-xs text-brand-coral font-semibold hover:underline" data-testid="clear-near">Clear location</button>
                        )}
                    </div>
                    {error && !loading && (
                        <div
                            className="mb-5 rounded-xl border border-brand-coral/40 bg-brand-coral/5 p-5 flex items-start justify-between gap-3"
                            data-testid="events-load-error"
                        >
                            <div>
                                <div className="font-heading font-bold text-brand-coral">Couldn't load events</div>
                                <p className="text-sm text-brand-ink mt-1">{error}</p>
                            </div>
                            <button
                                onClick={load}
                                data-testid="events-retry"
                                className="pill btn-coral px-4 py-2 text-sm font-semibold flex-shrink-0"
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-5">
                        {events.map((ev) => (
                            <Link key={ev.id} to={`/events/${ev.event_slug || ev.id}`} className="rounded-xl border border-brand-line bg-white card-hover flex flex-col overflow-hidden" data-testid={`event-card-${ev.id}`}>
                                <div className="aspect-[16/10] bg-brand-bone overflow-hidden relative">
                                    {ev.images && ev.images[0] ? (
                                        <img src={imageUrl(ev.images[0])} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                    ) : (
                                        <div className="w-full h-full grain flex items-center justify-center text-brand-mute text-xs">No image yet</div>
                                    )}
                                    <span className="absolute top-3 left-3 chip">{ev.status.replace("_", " ")}</span>
                                    {ev.distance_miles != null && (
                                        <span className="absolute top-3 right-3 bg-brand-forest text-white text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" data-testid={`distance-${ev.id}`}>
                                            <Compass size={12} /> {ev.distance_miles} mi
                                        </span>
                                    )}
                                </div>
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="flex items-center justify-between text-xs text-brand-mute">
                                        <span>{ev.confirmed_count}/{ev.total_booth_capacity} booths</span>
                                    </div>
                                    <h3 className="font-heading text-xl font-bold mt-2">{ev.name}</h3>
                                    <p className="text-sm text-brand-mute mt-1 line-clamp-2">{ev.description}</p>
                                    <div className="mt-3 flex flex-col gap-1.5 text-sm text-brand-ink/70">
                                        <span className="inline-flex items-center gap-2">
                                            <CalendarDays size={14} className="text-brand-coral" />
                                            {ev.event_date
                                                ? <>{fmtDate(ev.event_date)} • {ev.start_time || "Time TBD"}</>
                                                : <span className="italic text-brand-mute">{fmtMonth(ev.event_date_public)} — exact date after login</span>}
                                        </span>
                                        <span className="inline-flex items-center gap-2">
                                            <MapPin size={14} className="text-brand-coral" />
                                            {ev.location
                                                ? <>{[ev.location, ev.city, ev.state].filter(Boolean).join(", ")}</>
                                                : <span className="italic text-brand-mute">{[ev.city, ev.state].filter(Boolean).join(", ") || "Location"} — exact address after login</span>}
                                        </span>
                                        {ev.host?.company_name && (
                                            <span className="inline-flex items-center gap-2"><Users size={14} className="text-brand-coral" /> Hosted by {ev.host.company_name}</span>
                                        )}
                                        {ev.total_employees ? (
                                            <span className="inline-flex items-center gap-2"><Users size={14} className="text-brand-coral" /> {Number(ev.total_employees).toLocaleString()} employees at this location</span>
                                        ) : null}
                                    </div>
                                    <span className="mt-4 text-sm font-semibold text-brand-forest">View details →</span>
                                </div>
                            </Link>
                        ))}
                        {!loading && events.length === 0 && !error && (
                            <div className="md:col-span-2 rounded-xl border border-dashed border-brand-line p-12 text-center text-brand-mute">
                                {filters.near
                                    ? <>No events within {filters.radius_miles} miles of <span className="font-semibold">"{filters.near}"</span>. Try widening the radius or clearing the location.</>
                                    : "No published events match these filters yet."}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Shell>
    );
}
